import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { formatCurrency, formatDateTime as formatDate } from '../../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  CheckCircle,
  XCircle,
  Warning,
  Receipt,
  PaperPlaneTilt,
  CurrencyDollar,
  Spinner,
} from '@phosphor-icons/react';

const SOURCE_BADGE = {
  '835': { label: '835 ERA', color: 'bg-emerald-100 text-emerald-700' },
  '277CA': { label: '277CA', color: 'bg-amber-100 text-amber-700' },
  'TA1': { label: 'TA1', color: 'bg-red-100 text-red-700' },
  '999': { label: '999', color: 'bg-rose-100 text-rose-700' },
};

const HeadlineBanner = ({ headline, claimStatus }) => {
  if (!headline) {
    return (
      <Card className="p-4 bg-slate-50 border-slate-200" data-testid="adj-headline-empty">
        <p className="text-sm text-slate-600 flex items-center gap-2">
          <PaperPlaneTilt size={16} />
          {claimStatus === 'Draft'
            ? 'Not yet submitted to ODM.'
            : `Awaiting acknowledgement from ODM. Current status: ${claimStatus}`}
        </p>
      </Card>
    );
  }
  const isPaid = headline.source === '835' && /1|paid/i.test(String(headline.code || ''));
  const Icon = isPaid ? CheckCircle : headline.source === 'TA1' ? XCircle : Warning;
  const tone = isPaid
    ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
    : headline.source === 'TA1' || /R/i.test(String(headline.code || ''))
      ? 'bg-red-50 border-red-200 text-red-900'
      : 'bg-amber-50 border-amber-200 text-amber-900';
  return (
    <Card className={`p-4 border ${tone}`} data-testid="adj-headline">
      <div className="flex items-start gap-3">
        <Icon size={22} className="shrink-0 mt-0.5" weight="fill" />
        <div>
          <p className="font-semibold text-sm flex items-center gap-2 flex-wrap">
            {headline.code && <span className="font-mono text-xs">{headline.code}</span>}
            <span>{headline.label || 'See details below'}</span>
          </p>
          <p className="text-xs opacity-75 mt-1">
            Reported by ODM via {headline.source}
          </p>
        </div>
      </div>
    </Card>
  );
};

const Section = ({ title, badge, children, testid }) => (
  <Card className="p-4 space-y-3" data-testid={testid}>
    <div className="flex items-center justify-between">
      <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">{title}</h3>
      {badge && (
        <Badge className={`text-[10px] ${badge.color}`}>{badge.label}</Badge>
      )}
    </div>
    {children}
  </Card>
);

const KV = ({ k, v, mono }) => (
  <div className="flex justify-between gap-2 text-xs py-1 border-b border-slate-100 last:border-b-0">
    <span className="text-slate-500">{k}</span>
    <span className={`text-slate-900 ${mono ? 'font-mono' : ''}`}>{v ?? '—'}</span>
  </div>
);

export const AdjudicationDialog = ({ claimId, claimNumber, open, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open || !claimId) return;
    setLoading(true);
    setErr(null);
    setData(null);
    api
      .get(`/claims/${claimId}/adjudication`)
      .then((res) => setData(res.data))
      .catch((e) => setErr(e.response?.data?.detail || 'Failed to load adjudication'))
      .finally(() => setLoading(false));
  }, [open, claimId]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="adjudication-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt size={20} weight="duotone" className="text-indigo-600" />
            Adjudication for {claimNumber || claimId}
          </DialogTitle>
          <DialogDescription>
            Every piece of feedback ODM has returned for this claim — payment, rejection reasons, and envelope status.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex justify-center py-12" data-testid="adj-loading">
            <Spinner size={28} className="animate-spin text-indigo-600" />
          </div>
        )}

        {err && (
          <Card className="p-4 bg-red-50 border-red-200 text-red-700 text-sm" data-testid="adj-error">
            {err}
          </Card>
        )}

        {data && !loading && (
          <div className="space-y-3">
            <HeadlineBanner headline={data.headline} claimStatus={data.claim_status} />

            {data.era && (
              <Section
                title="Payment & adjustments (835 ERA)"
                badge={SOURCE_BADGE['835']}
                testid="adj-era-section"
              >
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <KV k="Status" v={data.era.status_label || data.era.status} />
                  <KV k="Status code" v={data.era.status_code} mono />
                  <KV k="Paid" v={formatCurrency(data.era.paid_amount)} mono />
                  <KV k="Adjusted" v={formatCurrency(data.era.adjustment_amount)} mono />
                  <KV k="Patient responsibility" v={formatCurrency(data.era.patient_responsibility)} mono />
                  <KV k="Payer claim ID" v={data.era.payer_claim_id} mono />
                  <KV k="Processed at" v={formatDate(data.era.processed_at)} />
                </div>
                {data.era.adjustments?.length > 0 && (
                  <div className="pt-3 mt-1 border-t border-slate-100">
                    <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">
                      Adjustments ({data.era.adjustments.length})
                    </p>
                    <div className="space-y-2">
                      {data.era.adjustments.map((a, i) => (
                        <div
                          key={`${a.group_code}-${a.reason_code}-${i}`}
                          className="bg-slate-50 rounded-md p-2.5 text-xs"
                          data-testid={`adj-adjustment-${i}`}
                        >
                          <div className="flex justify-between items-start gap-2">
                            <span className="font-mono text-[11px] text-slate-700 shrink-0">
                              {a.group_code}-{a.reason_code}
                            </span>
                            <span className="font-mono text-slate-900 font-semibold shrink-0">
                              {formatCurrency(a.amount)}
                            </span>
                          </div>
                          <p className="text-slate-700 mt-1 leading-relaxed">{a.reason_label}</p>
                          {a.group_label && (
                            <p className="text-slate-500 text-[10px] mt-0.5">
                              {a.group_code} = {a.group_label}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Section>
            )}

            {data.ca_277 && (
              <Section
                title="Claim status (277CA)"
                badge={SOURCE_BADGE['277CA']}
                testid="adj-277-section"
              >
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <KV k="Status" v={data.ca_277.status} />
                  <KV k="Status code" v={data.ca_277.status_code} mono />
                  <KV k="Claim status" v={data.ca_277.claim_status} />
                  <KV k="Action" v={data.ca_277.action} />
                  <KV k="Received at" v={formatDate(data.ca_277.received_at)} />
                </div>
                {data.ca_277.errors?.length > 0 && (
                  <div className="pt-3 mt-1 border-t border-slate-100">
                    <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">
                      Reasons ({data.ca_277.errors.length})
                    </p>
                    <div className="space-y-2">
                      {data.ca_277.errors.map((e, i) => (
                        <div
                          key={`${e.code || 'noc'}-${i}`}
                          className="bg-amber-50/60 border border-amber-100 rounded-md p-2.5 text-xs"
                          data-testid={`adj-277-err-${i}`}
                        >
                          <div className="flex items-start gap-2">
                            {e.code && <span className="font-mono text-[11px] text-amber-700 shrink-0">{e.code}</span>}
                            <span className="text-slate-800 leading-relaxed">
                              {e.description || e.message || JSON.stringify(e)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Section>
            )}

            {data.batch && (
              <Section
                title="Submission envelope (TA1)"
                badge={data.batch.ta1_status_code === 'R' ? SOURCE_BADGE['TA1'] : { label: 'Batch', color: 'bg-slate-100 text-slate-700' }}
                testid="adj-batch-section"
              >
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <KV k="Batch ID" v={data.batch.batch_id} mono />
                  <KV k="Filename" v={<span className="font-mono text-[10px] break-all">{data.batch.filename}</span>} />
                  <KV k="Submitted" v={formatDate(data.batch.submitted_at)} />
                  <KV k="TA1 status" v={data.batch.ta1_status} />
                  <KV k="TA1 note code" v={data.batch.ta1_note_code} mono />
                  <KV k="TA1 note" v={data.batch.ta1_note_message} />
                  <KV k="TA1 received at" v={formatDate(data.batch.ta1_received_at)} />
                </div>
                <a
                  href={`/odm/submissions/${encodeURIComponent(data.batch.batch_id)}`}
                  className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline mt-1"
                >
                  View full batch lifecycle →
                </a>
              </Section>
            )}

            {data.batch?.error_report_errors?.length > 0 && (
              <Section
                title="ODM compliance check (HTML error report)"
                badge={SOURCE_BADGE['ErrorReport']}
                testid="adj-error-report-section"
              >
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <KV k="Received" v={formatDate(data.batch.error_report_received_at)} />
                  <KV k="Summary" v={data.batch.error_report_summary} />
                </div>
                <div className="pt-3 mt-1 border-t border-slate-100">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">
                    Errors ({data.batch.error_report_errors.length})
                  </p>
                  <div className="space-y-2">
                    {data.batch.error_report_errors.map((e, i) => (
                      <div
                        key={`${e.segment || ''}-${e.position || ''}-${e.element || ''}-${i}`}
                        className="bg-orange-50/60 border border-orange-100 rounded-md p-2.5 text-xs"
                        data-testid={`adj-er-err-${i}`}
                      >
                        <div className="flex items-start gap-2 mb-1">
                          <span className="font-mono text-[11px] text-orange-700 shrink-0">
                            {e.segment}{e.position ? `@${e.position}` : ''} · {e.element}
                          </span>
                          <span className="text-[10px] text-slate-500 shrink-0">loop {e.loop}</span>
                        </div>
                        <p className="text-slate-800 leading-relaxed font-medium">
                          {e.error_description || e.error_code_label}
                        </p>
                        {e.data_content && (
                          <p className="text-slate-500 mt-1 font-mono text-[10px]">
                            <span className="text-slate-400">value:</span> {e.data_content}
                          </p>
                        )}
                        {e.error_code && (
                          <p className="text-[10px] text-slate-500 mt-0.5">
                            Error code {e.error_code}{e.error_code_label ? ` · ${e.error_code_label}` : ''}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </Section>
            )}

            {!data.era && !data.ca_277 && !data.batch?.ta1_status && (
              <Card className="p-4 bg-slate-50 text-center text-sm text-slate-600" data-testid="adj-no-data">
                <CurrencyDollar size={28} className="mx-auto mb-2 text-slate-300" />
                No ODM feedback yet. Once ODM returns a TA1, 999, 277CA, or 835 for this claim, full details will appear here.
              </Card>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
